<script>
import {ElMessage, ElNotification} from "element-plus";
import {Download, Upload} from "@element-plus/icons-vue";
import ExportHubParamField from "@/views/export-hub/ExportHubParamField.vue";
import {
  visibleParams,
  fileParam,
  isValueFilled,
  downloadExport,
  uploadImport,
  extractErrorMessage,
  isAsyncJobResponse,
  pollJobStatus,
  hasLangHeader,
  LANG_OPTIONS,
} from "@/views/export-hub/exportHubApi";

export default {
  name: "ExportParamModal",
  components: {ExportHubParamField, Download, Upload},
  props: {
    visible: {type: Boolean, default: false},
    item: {type: Object, default: null},
    globalBoardId: {default: null},
    boardsOptions: {type: Array, default: () => []},
    boardsLoading: {type: Boolean, default: false},
  },
  emits: ["update:visible", "ensure-boards", "success"],
  data() {
    return {
      values: {},
      lang: "uz_cyr",
      langOptions: LANG_OPTIONS,
      file: null,
      dragActive: false,
      submitting: false,
      errorMessage: "",
      regionsOptions: [],
      regionsLoading: false,
      districtsOptions: [],
      districtsLoading: false,
      quartersOptions: [],
      quartersLoading: false,
      stopPolling: null,
    };
  },
  computed: {
    isImport() {
      return this.item?.kind === "import";
    },
    showLangSelect() {
      return hasLangHeader(this.item);
    },
    hasFileField() {
      return this.isImport && !!fileParam(this.item);
    },
    requiredParams() {
      return this.item ? visibleParams(this.item).filter((p) => p.required) : [];
    },
    optionalParams() {
      return this.item ? visibleParams(this.item).filter((p) => !p.required) : [];
    },
    maxSizeMb() {
      const m = /(\d+)\s*MB/i.exec(this.item?.description || "");
      return m ? Number(m[1]) : null;
    },
    canSubmit() {
      if (this.submitting || !this.item) return false;
      if (this.hasFileField && !this.file) return false;
      return this.requiredParams.every((p) => isValueFilled(this.values[p.name]));
    },
  },
  watch: {
    visible(v) {
      if (v && this.item) this.initValues(this.item);
    },
  },
  methods: {
    initValues(item) {
      const values = {};
      (item.params || []).forEach((p) => {
        if (p.type === "file") return;
        if (p.name === "board_id") {
          values[p.name] = this.globalBoardId ?? null;
        } else if (p.name === "year" || p.name === "fin_year") {
          values[p.name] = String(new Date().getFullYear());
        } else {
          values[p.name] = null;
        }
      });
      this.values = values;
      this.lang = "uz_cyr";
      this.file = null;
      this.errorMessage = "";
      this.regionsOptions = [];
      this.districtsOptions = [];
      this.quartersOptions = [];
    },
    onClosed() {
      this.values = {};
      this.file = null;
      this.errorMessage = "";
      this.dragActive = false;
    },
    formatSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    },
    setFile(f) {
      if (!/\.(xlsx|xls)$/i.test(f.name)) {
        ElMessage.warning("Faqat .xlsx yoki .xls fayllar qabul qilinadi");
        return;
      }
      if (this.maxSizeMb && f.size > this.maxSizeMb * 1024 * 1024) {
        ElMessage.warning(`Fayl hajmi ${this.maxSizeMb}MB dan oshmasligi kerak`);
        return;
      }
      this.file = f;
    },
    onFileInputChange(e) {
      const f = e.target.files[0];
      e.target.value = "";
      if (f) this.setFile(f);
    },
    onDrop(e) {
      this.dragActive = false;
      const f = e.dataTransfer.files[0];
      if (f) this.setFile(f);
    },
    clearFile() {
      this.file = null;
    },
    ensureRegionsLoaded() {
      this.regionsLoading = true;
      this.$http
          .get("api/v1/regions")
          .then(({data}) => {
            this.regionsOptions = (data?.regions || []).map((r) => ({value: r.id, label: r.title}));
          })
          .catch(() => {
            ElMessage.warning("Viloyatlar ro'yxatini yuklab bo'lmadi");
          })
          .finally(() => {
            this.regionsLoading = false;
          });
    },
    ensureDistrictsLoaded() {
      const params = {};
      if (isValueFilled(this.values.region_id)) params.region_id = this.values.region_id;
      this.districtsLoading = true;
      this.$http
          .get("api/v1/districts", {params})
          .then(({data}) => {
            this.districtsOptions = (data?.districts || []).map((d) => ({value: d.id, label: d.title}));
          })
          .catch(() => {
            ElMessage.warning("Tumanlar ro'yxatini yuklab bo'lmadi");
          })
          .finally(() => {
            this.districtsLoading = false;
          });
    },
    ensureQuartersLoaded() {
      const params = {};
      if (isValueFilled(this.values.district_id)) params.district_id = this.values.district_id;
      if (isValueFilled(this.values.region_id)) params.region_id = this.values.region_id;
      this.quartersLoading = true;
      this.$http
          .get("api/v1/quarters", {params})
          .then(({data}) => {
            this.quartersOptions = (data?.quarters || []).map((q) => ({value: q.id, label: q.title}));
          })
          .catch(() => {
            ElMessage.warning("Mahallalar ro'yxatini yuklab bo'lmadi");
          })
          .finally(() => {
            this.quartersLoading = false;
          });
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = "";
      try {
        if (this.isImport) {
          const res = await uploadImport(this.$http, this.item, this.file, this.values);
          this.handleImportSuccess(res);
        } else {
          const headers = this.showLangSelect ? {hl: this.lang} : undefined;
          await downloadExport(this.$http, this.item, this.values, headers);
          ElNotification({
            title: "Muvaffaqiyatli",
            message: `"${this.item.title}" fayli yuklab olindi`,
            type: "success",
          });
          this.$emit("success", this.item);
          this.$emit("update:visible", false);
        }
      } catch (error) {
        this.errorMessage = await extractErrorMessage(
            error,
            this.isImport ? "Faylni yuklashda xatolik yuz berdi." : "Faylni yuklab olishda xatolik yuz berdi."
        );
      } finally {
        this.submitting = false;
      }
    },
    handleImportSuccess(res) {
      const data = res?.data;
      const title = this.item.title;
      if (isAsyncJobResponse(data)) {
        ElNotification({
          title: "Import jarayonda",
          message: `"${title}" fayli qabul qilindi, qayta ishlanmoqda...`,
          type: "info",
          duration: 6000,
        });
        this.$emit("success", this.item);
        this.$emit("update:visible", false);
        pollJobStatus(this.$http, data.status_endpoint, (result) => {
          if (!result) {
            ElNotification({
              title: "Import holati noaniq",
              message: "Holatni tekshirib bo'lmadi, keyinroq qayta urinib ko'ring.",
              type: "warning",
            });
            return;
          }
          ElNotification({
            title: "Import yakunlandi",
            message: result.message || `"${title}" muvaffaqiyatli qayta ishlandi`,
            type: "success",
          });
        });
        return;
      }
      let extra = "";
      if (Array.isArray(data?.not_found_schools) && data.not_found_schools.length) {
        extra = ` Topilmadi: ${data.not_found_schools.join(", ")}`;
      }
      ElNotification({
        title: "Muvaffaqiyatli",
        message: `"${title}" muvaffaqiyatli yuklandi.${extra}`,
        type: "success",
      });
      this.$emit("success", this.item);
      this.$emit("update:visible", false);
    },
  },
};
</script>

<template>
  <el-dialog
      :model-value="visible"
      :title="item ? item.title : ''"
      width="580px"
      append-to-body
      :close-on-click-modal="!submitting"
      :show-close="!submitting"
      @update:model-value="(v) => $emit('update:visible', v)"
      @closed="onClosed"
  >
    <template v-if="item">
      <p class="eh-modal__desc">{{ item.description }}</p>
      <el-alert v-if="item.note" type="warning" :closable="false" show-icon class="eh-modal__note">
        {{ item.note }}
      </el-alert>

      <div v-if="showLangSelect" class="eh-lang">
        <span class="eh-lang__label">Fayl tili</span>
        <el-select v-model="lang" class="eh-lang__select">
          <el-option v-for="o in langOptions" :key="o.value" :value="o.value" :label="o.label"/>
        </el-select>
      </div>
      <el-alert
          v-if="errorMessage"
          type="error"
          show-icon
          class="eh-modal__error"
          @close="errorMessage = ''"
      >
        {{ errorMessage }}
      </el-alert>

      <div
          v-if="hasFileField"
          class="eh-dropzone"
          :class="{'eh-dropzone--active': dragActive, 'eh-dropzone--filled': !!file}"
          @dragover.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="onDrop"
          @click="!file && $refs.fileInput.click()"
      >
        <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            class="eh-dropzone__input"
            @change="onFileInputChange"
        />
        <template v-if="!file">
          <p class="eh-dropzone__text">
            Faylni shu yerga tashlang yoki <span class="eh-dropzone__link">tanlang</span>
          </p>
          <p class="eh-dropzone__hint">
            .xlsx yoki .xls<span v-if="maxSizeMb"> — maksimum {{ maxSizeMb }}MB</span>
          </p>
        </template>
        <template v-else>
          <p class="eh-dropzone__text">{{ file.name }} ({{ formatSize(file.size) }})</p>
          <el-button size="small" text type="danger" @click.stop="clearFile">O'chirish</el-button>
        </template>
      </div>

      <div v-if="requiredParams.length" class="eh-fields">
        <ExportHubParamField
            v-for="p in requiredParams"
            :key="p.name"
            v-model="values[p.name]"
            :param="p"
            :boards-options="boardsOptions"
            :boards-loading="boardsLoading"
            :regions-options="regionsOptions"
            :regions-loading="regionsLoading"
            :districts-options="districtsOptions"
            :districts-loading="districtsLoading"
            :quarters-options="quartersOptions"
            :quarters-loading="quartersLoading"
            @focus-board="$emit('ensure-boards')"
            @focus-region="ensureRegionsLoaded"
            @focus-district="ensureDistrictsLoaded"
            @focus-quarter="ensureQuartersLoaded"
        />
      </div>

      <el-collapse v-if="optionalParams.length" class="eh-optional">
        <el-collapse-item title="Qo'shimcha filtrlar" name="optional">
          <ExportHubParamField
              v-for="p in optionalParams"
              :key="p.name"
              v-model="values[p.name]"
              :param="p"
              :boards-options="boardsOptions"
              :boards-loading="boardsLoading"
              :regions-options="regionsOptions"
              :regions-loading="regionsLoading"
              :districts-options="districtsOptions"
              :districts-loading="districtsLoading"
              :quarters-options="quartersOptions"
              :quarters-loading="quartersLoading"
              @focus-board="$emit('ensure-boards')"
              @focus-region="ensureRegionsLoaded"
              @focus-district="ensureDistrictsLoaded"
              @focus-quarter="ensureQuartersLoaded"
          />
        </el-collapse-item>
      </el-collapse>
    </template>

    <template #footer>
      <el-button :disabled="submitting" @click="$emit('update:visible', false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">
        <el-icon v-if="!submitting" style="margin-right: 4px">
          <Upload v-if="isImport"/>
          <Download v-else/>
        </el-icon>
        {{ isImport ? "Yuklash" : "Yuklab olish" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.eh-modal__desc {
  margin: 0 0 12px;
  font-size: 13px;
  color: #666;
}

.eh-modal__note,
.eh-modal__error {
  margin-bottom: 14px;
}

.eh-lang {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  &__label {
    font-size: 13px;
    color: #666;
  }

  &__select {
    width: 200px;
  }
}

.eh-fields,
.eh-optional {
  margin-top: 4px;
}

.eh-optional {
  margin-bottom: 4px;

  :deep(.el-collapse-item__content) {
    padding-top: 8px;
    padding-bottom: 4px;
  }
}

.eh-dropzone {
  position: relative;
  border: 1.5px dashed rgba(66, 78, 121, 0.3);
  border-radius: 8px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 16px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover,
  &--active {
    border-color: var(--el-color-primary);
    background: rgba(0, 119, 145, 0.04);
  }

  &--filled {
    cursor: default;
    border-style: solid;
  }

  &__input {
    display: none;
  }

  &__text {
    margin: 0;
    font-size: 13px;
    color: #333;
  }

  &__link {
    color: var(--el-color-primary);
    text-decoration: underline;
  }

  &__hint {
    margin: 6px 0 0;
    font-size: 11px;
    color: #999;
  }
}
</style>
